import React, { Component } from 'react'
import { RouteComponentProps } from 'react-router-dom'
import { AxiosInstance } from 'axios'
import { withTranslation, WithTranslation } from 'react-i18next'
import { getmeta, Meta } from '../common'
import { Breadcrumb, Button, Confirm, Container, Grid, GridColumn, Icon, Input, Loader, List, ListContent, ListHeader, ListIcon, ListItem, Message, Modal, Segment, Dimmer } from 'semantic-ui-react'
import FileView from './FileView'
import "./Files.css"
import Dropzone from 'react-dropzone'
import FileSelect from './FileSelect'

interface FilesState {
    loading: boolean,
    errorMsg: string,
    listing: any[],
    viewFile: string,
    reqDeleteFile: string,
    reqFileUpload: boolean,
    reqNewDir: string|null,
    reqRenameType: string,
    renameDstType: string,
}

interface FilesProps extends WithTranslation, RouteComponentProps {
    meta: Meta,
    http: AxiosInstance
}

class Files extends Component<FilesProps, FilesState> {

    state = {
        loading: false,
        errorMsg: '',
        listing: [],
        viewFile: '',
        reqDeleteFile: '',
        reqFileUpload: false,
        reqNewDir: null,
        reqRenameType: '',
        renameDstType: ''
    }

    getListing(path: string) {
        const { t, meta } = this.props
        this.setState({loading: true})
        const dirpath = (path.endsWith('/') ? path : path+'/')
        const url = 'files/'+dirpath
        this.props.http.get(url)
            .then(response => {
                const data = response.data.data
                const listing = this.filterFirstLevel(this.addMeta(dirpath, data))
                this.setState({listing: listing, errorMsg: '', loading: false})
            }).catch(err => {
                if (err.response) {
                    let msg = ''
                    const type = path.replace(/^_/,'').replaceAll('/', ':')
                    if (err.response.status!==404 || !Object.keys(meta.resources['user_files']).includes(type)) {
                        msg = err.response.data.message||err.response.data
                    }
                    this.setState({errorMsg: msg, loading: false, listing: []})
                } else {
                    this.setState({errorMsg: t('Unknown error'), loading: false, listing: []})
                }
            })
    }

    deleteFile(fname: string) {
        const { t } = this.props
        const url = 'files/'+fname
        this.setState({loading: true})
        this.props.http.delete(url)
            .then(response => {
                this.setState({reqDeleteFile: '', errorMsg: '', loading: false})
                this.getListing(this.props.match.params[0]||'')
            }).catch(err => {
                if (err.response) {
                    this.setState({errorMsg: err.response.data.message||err.response.data, reqDeleteFile: '', loading: false})
                } else {
                    this.setState({errorMsg: t('Unknown error'), loading: false})
                }
            })
    }

    renameFile(srcType, dstType) {
        const { t } = this.props
        const fname = srcType.replace(/^:/,'_/').replaceAll(':', '/')
        const data = {rename: dstType}
        const url = 'files/'+fname
        this.setState({loading: true, reqRenameType: ''})
        this.props.http.post(url, data)
            .then(response => {
                this.setState({errorMsg: '', loading: false})
                this.getListing(this.props.match.params[0]||'')
            }).catch(err => {
                if (err.response) {
                    this.setState({errorMsg: err.response.data.message||err.response.data, loading: false})
                } else {
                    this.setState({errorMsg: t('Unknown error'), loading: false})
                }
            })
    }

    uploadFiles(files: File[]|null) {
        if (!files) return
        const { http, t } = this.props
        const path = this.props.match.params[0]
        const dirpath = (path.endsWith('/') ? path : path+'/')
        this.setState({loading: true})
        for (let file of files) {
            var formData = new FormData()
            // "path" attribute applies when a whole folder of files is being uploaded recursively
            const fname = (file['path'] || file.name).replace(/^\//,'')
            formData.append("file", file)
            http.put('/files/'+dirpath+fname, formData, {headers: {'Content-Type': 'multipart/form-data'}})
            .then(response => {
                this.setState({errorMsg: '', loading: false})
                this.getListing(this.props.match.params[0]||'')
            }).catch(err => {
                if (err.response) {
                    this.setState({errorMsg: err.response.data.message||err.response.data, loading: false})
                } else {
                    this.setState({errorMsg: t('Unknown error'), loading: false})
                }
            })
        }
    }

    mkdir(name: string) {
        const { http, t } = this.props
        const path = this.props.match.params[0]
        const newDirPath = (path.endsWith('/') ? path : path+'/') + name + '/'
        this.setState({loading: true})
        http.put('/files/'+newDirPath)
        .then(response => {
            this.setState({errorMsg: '', loading: false})
            this.getListing(this.props.match.params[0]||'')
        }).catch(err => {
            if (err.response) {
                this.setState({errorMsg: err.response.data.message||err.response.data, loading: false})
            } else {
                this.setState({errorMsg: t('Unknown error'), loading: false})
            }
        })
    }

    addMeta(path: string, data: any[]) {
        const { meta } = this.props
        const type = path.replace(/^_/, '').replaceAll('/', ':')
        let list = data.map(i => ({label: i.name, ...i, meta: getmeta(meta, 'user_files', type+i.name, 'resources')}))
        // add subtypes which may not have their folder physically created yet
        const userFiles = meta['resources']['user_files']
        if (type) {
            for (let ftype of Object.keys(userFiles)) {
                if (!ftype.startsWith(type)) continue
                const sub = userFiles[ftype]['type'].substring(type.length).split(':')[0]
                if (!list.filter(x => x.name===sub).length) {
                    list.push({
                        label: sub,
                        name: sub,
                        size: 0,
                        ctime: null,
                        mtime: null,
                        dir: true,
                        meta: userFiles[ftype]
                    })
                }
            }
        }
        // sort directories/folders/types first, then by name
        return list.sort((a,b) => (a.dir && !b.dir) ? -1 : ((!a.dir && b.dir) ? 1 : (
                    (a.name < b.name) ? -1 : ((a.name > b.name) ? 1 : 0))
                ))
    }

    filterFirstLevel(list) {
        const { meta, t } = this.props
        if (this.props.match.params[0]) return list
        else {
            let global = list.find(x => x.name==='_global')
            let users = list.find(x => x.name===meta.user['id'])
            let ret: any[] = []
            if (global) {
                global.label = t('Global')
                global.name = '_/'
                ret.push(global)
            }
            if (users) {
                users.label = t('Private')
                ret.push(users)
            }
            return ret
        }
    }

    componentDidMount(): void {
        this.getListing(this.props.match.params[0]||'')
    }

    componentDidUpdate(prevProps: Readonly<FilesProps>, prevState: Readonly<FilesState>, snapshot?: any): void {
        const { loading } = this.state
        if (loading) return
        const path = this.props.match.params[0]
        if (path!==prevProps.match.params[0]) {
            this.getListing(path||'')
        }
    }

    render() {
        const { t, history, meta, http } = this.props
        const { loading, listing, viewFile, reqDeleteFile, reqFileUpload, reqNewDir, reqRenameType, renameDstType } = this.state
        const path = this.props.match.params[0]
        const dirpath = (!path||path.endsWith('/') ? path : path+'/')
        const type = path.replaceAll('/',':').replace(/^_/, '')
        const myMeta = getmeta(meta, 'user_files', type, 'resources')

        const open = (path, i) => {
            if (i.dir) {
                history.push('/files/'+path+i.name)
            } else {
                this.setState({viewFile: path+i.name})
            }
        }

        const parts = dirpath.substring(0, dirpath.length-1).split('/').filter(x => x)
        const pathSections = parts.reduce((acc, cur, i) => {
            let label = cur
            if (i===0) {
                if (label==='_') label = t('Global')
                else if (label===meta.user['id']) label = t('Private')
            }
            const link = i!==parts.length-1
            const active = !link
            const curpath = [...acc.map(x => x.key), cur].join('/')
            let sec = {
                key: cur,
                content: label,
                link: link,
                active: active,
                onClick: link ? () => history.push('/files/'+curpath) : undefined
            }
            acc.push(sec)
            return acc
        }, [])
        const sections = [{
            key: '',
            content: <Icon name="home"/>,
            onClick: path ? () => history.push('/files/') : undefined
        }].concat(pathSections)

        function fileSizeIEC(size) {
            var i = size == 0 ? 0 : Math.floor(Math.log(size) / Math.log(1024))
            return +((size / Math.pow(1024, i)).toFixed(2)) * 1 + ' ' + ['B', 'kiB', 'MiB', 'GiB', 'TiB'][i]
        }

        return(
            <>
            <>{this.state.errorMsg ?
              (
                <Container>
                    <Message error icon='exclamation triangle' header={t('Error')} content={this.state.errorMsg}/>
                </Container>
              )
              : null }
            </>
            <Container classNames="files">
            <Modal size="fullscreen" open={!!viewFile} closeIcon={true} onClose={() => this.setState({viewFile: ''})} centered={false}>
                <Modal.Content>
                    <FileView path={viewFile} {...this.props}/>
                </Modal.Content>
            </Modal>
            <Modal open={reqFileUpload} closeIcon={true} onClose={() => this.setState({reqFileUpload: false})} centered={false}>
                <Modal.Header>{t('Upload file')}</Modal.Header>
                <Modal.Content>
                    <Grid>
                        <Grid.Row>
                            <Grid.Column width={16}>
                                <Input type="file" onChange={(e, d) => {
                                    this.setState({reqFileUpload: false})
                                    this.uploadFiles(Array.from(e.target.files||[]))}
                                    }/>
                            </Grid.Column>
                        </Grid.Row>
                        <Grid.Row>
                            <Grid.Column width={12}>
                                {/*<Button type="button" color="blue" icon="upload" content={t("Upload")} onClick={()=>{}}/>*/}
                            </Grid.Column>
                            <Grid.Column width={4} textAlign='right'>
                                <Button type="button" content={t('Cancel')} onClick={()=>this.setState({reqFileUpload: false})}/>
                            </Grid.Column>
                        </Grid.Row>
                    </Grid>
                </Modal.Content>
            </Modal>
            <Modal open={reqNewDir!==null} closeIcon={true} onClose={() => this.setState({reqNewDir: null})} centered={false}>
                <Modal.Header>{t('New folder')}</Modal.Header>
                <Modal.Content>
                    <Grid>
                        <Grid.Row>
                            <Grid.Column width={16}>
                                <Input id="new_dir" onChange={(e, {value})=>this.setState({reqNewDir: value})}/>
                            </Grid.Column>
                        </Grid.Row>
                        <Grid.Row>
                            <Grid.Column width={12}>
                                <Button type="button" color="blue" onClick={()=>{
                                        this.mkdir(reqNewDir||'')
                                        this.setState({reqNewDir: null})
                                    }} disabled={!reqNewDir}>
                                    {t("Create")}
                                </Button>
                            </Grid.Column>
                            <Grid.Column width={4} textAlign='right'>
                                <Button type="button" content={t('Cancel')} onClick={()=>this.setState({reqNewDir: null})}/>
                            </Grid.Column>
                        </Grid.Row>
                    </Grid>
                </Modal.Content>
            </Modal>
            <Modal open={reqRenameType!==''} closeIcon={true} onClose={() => this.setState({reqRenameType: ''})} centered={false}>
                <Modal.Header>{t('New destination and name')}</Modal.Header>
                <Modal.Content>
                    <Grid>
                    <Grid.Row>
                        <Grid.Column width={16}>
                            <FileSelect {...this.props} initialType={reqRenameType.replace(/:[^:]*$/, '')} rootType={''} setSelected={(type) => this.setState({renameDstType: type})} 
                                rename={reqRenameType.split(':').at(-1)}/>
                        </Grid.Column>
                    </Grid.Row>
                    <Grid.Row>
                        <Grid.Column width={12}>
                            <Button button color="orange" content={t('Rename')} onClick={()=>this.renameFile(reqRenameType, renameDstType)}
                                disabled={renameDstType==='' || renameDstType===reqRenameType || renameDstType.endsWith(':')}/>
                        </Grid.Column>
                        <Grid.Column width={4} textAlign='right'>
                            <Button button color="grey" content={t('Close')} onClick={() => this.setState({reqRenameType: ''})}/>
                        </Grid.Column>
                    </Grid.Row>
                    </Grid>
                </Modal.Content>
            </Modal>
            <Confirm open={!!reqDeleteFile} cancelButton={t('Cancel')} confirmButton={t('Delete')}
                onCancel={()=>this.setState({reqDeleteFile:''})} onConfirm={()=>this.deleteFile(reqDeleteFile)}
                content={t("Do you really want to delete '{{filename}}'?", {filename: reqDeleteFile.replaceAll('/',':').replace(/^_/,'')})}/>
            <Grid className="compact">
                <Grid.Row verticalAlign='middle'>
                    <Grid.Column width={12}>
                        <Breadcrumb sections={sections} size='big'/>
                    </Grid.Column>
                    <GridColumn width={4} textAlign='right'>
                        {myMeta && myMeta._can_write ?
                        <>
                        <Button type="button" color="blue" icon="upload" size="tiny" title={t('Upload')}
                            onClick={()=>this.setState({reqFileUpload: true})}/>
                        <Button type="button" size="tiny" color="blue" icon="folder" title={t('New folder')}
                            onClick={()=>{this.setState({reqNewDir:''})}}/>
                        </>
                        : null }
                    </GridColumn>
                </Grid.Row>
                <Grid.Row>
                    <Grid.Column width={16}>
                    <Segment className="files">
                        { loading? 
                        <Dimmer active inverted><Loader inverted/></Dimmer> 
                        :
                        <Dropzone onDrop={files => this.uploadFiles(files)}>
                        {({getRootProps, getInputProps}) => (
                            <List {...getRootProps({onClick: (e)=>{e.stopPropagation()}})}>
                                {listing.map((i: any) => (
                                    <ListItem disabled={!i.meta._can_read} 
                                        title={t('size: {{size}}, last change: {{mtime}}, created: {{ctime}}',
                                            {
                                                size: fileSizeIEC(i.size), 
                                                ctime: i.ctime ? new Date(i.ctime.replace(' ','T')).toLocaleString() : '-',
                                                mtime: i.mtime ? new Date(i.mtime.replace(' ','T')).toLocaleString() : '-'
                                            })}>
                                        <ListIcon name={i.meta._can_read ? (i.dir ? 'folder' : 'file') : 'lock'}/>
                                        <ListContent>
                                            <ListHeader>
                                                <div className="label" onClick={() => open(dirpath, i)}>
                                                    {i.label}
                                                </div>
                                                {i.meta._can_write && i.meta.type.indexOf(':') > -1 ? 
                                                <div className="icons">
                                                    <Button type="button" color="blue" icon="edit" size="tiny" title={t('Rename or move')}
                                                        onClick={(e,d)=>{this.setState({reqRenameType: (dirpath+i.name).replaceAll('/',':').replace(/^_/,'')});e.preventDefault()}}/>
                                                    { parts.slice(-1)[0] === 'archive' || i.name === 'archive' ? null :
                                                        <Button type="button" color="yellow" icon="archive" size="tiny" title={t('Archive')}
                                                        onClick={(e,d)=>{this.renameFile((dirpath+i.name).replaceAll('/',':').replace(/^_/,''), (dirpath+'archive/'+i.name).replaceAll('/',':').replace(/^_/,''));e.preventDefault()}}/>
                                                    }
                                                    <Button type="button" color="red" icon="delete" size="tiny" title={t('Delete')}
                                                        onClick={(e,d)=>{this.setState({reqDeleteFile: dirpath+i.name});e.preventDefault()}}/>
                                                </div>
                                                : null}
                                            </ListHeader>
                                        </ListContent>
                                    </ListItem>
                                ))}
                            <input {...getInputProps()} />
                            </List>
                        )}
                        </Dropzone>
                        }
                    </Segment>
                    </Grid.Column>
                </Grid.Row>
            </Grid>
            </Container>
        </>)
    }
}

export default withTranslation()(Files)